import { useEffect, useState, useMemo } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { ExcelImportModal } from "@/pages/Backups";
import { PrinterIcon, EyeIcon, EyeSlashIcon, FlowerIcon, ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
import { useEsMovil } from "@/lib/useMedia";

const NIVELES = [
  { k: "p0", l: "P0" }, { k: "p1", l: "P1" }, { k: "p2", l: "P2" }, { k: "p3", l: "P3" }, { k: "p4", l: "P4" },
];

export default function PriceList() {
  const { user } = useAuth();
  const canCosts = user?.role === "admin";               // PC visible solo para quien puede ver costos
  // Cargar precios desde Excel: exclusivo del Administrador General (el servidor también lo exige)
  const esAdminGeneral = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const [preciosOpen, setPreciosOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const sinCosto = products.filter((x) => x.activo !== false && x.tipo !== "servicio" && !(Number(x.costo_referencia) > 0)).length;
  const [loading, setLoading] = useState(true);
  const [filtroCat, setFiltroCat] = useState("");
  const [filtroMarca, setFiltroMarca] = useState("");
  const [q, setQ] = usePageState("q", "");
  const [showPC, setShowPC] = useState(false);            // PC PEPS oculto por defecto
  const [showInactive, setShowInactive] = useState(false);
  const [niveles, setNiveles] = useState(["p0", "p1", "p2", "p3", "p4"]);
  const [companyName, setCompanyName] = useState("AGROPORT Honduras");
  const movil = useEsMovil();     // teléfono: filtros compactos y tarjetas; computadora: tabla (sin cambios)

  const load = async (inact = showInactive) => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: inact ? { include_inactive: true } : {} });
      setProducts(data);
      try {
        const { data: cos } = await api.get("/companies");
        if (cos && cos[0]?.nombre) setCompanyName(cos[0].nombre);
      } catch { /* tolerable */ }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const reactivate = async (p) => {
    try { await api.post(`/products/${p.id}/reactivate`); toast.success(`${p.codigo} reactivado`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleNivel = (k) => {
    const next = niveles.includes(k) ? niveles.filter((x) => x !== k) : [...niveles, k];
    if (next.length === 0) { toast.error("Selecciona al menos un nivel de precio"); return; }
    setNiveles(NIVELES.map((n) => n.k).filter((x) => next.includes(x)));
  };

  const categorias = Array.from(new Set(products.map((p) => p.categoria).filter(Boolean))).sort();
  const marcas = Array.from(new Set(products.map((p) => p.marca).filter(Boolean))).sort();

  const base = useMemo(() => products
    .filter((p) => showInactive ? true : p.activo !== false)
    .filter((p) => p.tipo !== "no_inventariable") // no_inventariable no se vende
    .filter((p) => !filtroCat || p.categoria === filtroCat)
    .filter((p) => !filtroMarca || p.marca === filtroMarca)
    .sort((a, b) => `${a.categoria || ""}${a.codigo}`.localeCompare(`${b.categoria || ""}${b.codigo}`)), [products, showInactive, filtroCat, filtroMarca]);
  const busqueda = useLiveSearch(base, (p) => [p.codigo, p.sku, p.nombre, p.descripcion, p.categoria, p.marca, p.codigo_barras], { estado: [q, setQ] });
  const filtered = busqueda.resultados;

  const pcVisible = canCosts && showPC;
  const nCols = 4 + (pcVisible ? 1 : 0) + niveles.length;
  const doPrint = () => window.print();

  return (
    <Layout
      title="Lista de precios"
      subtitle={`${filtered.length} productos · Niveles: ${niveles.map((n) => n.toUpperCase()).join(", ")}${pcVisible ? " · Con PC PEPS" : ""}`}
      actions={
        <div className="flex gap-2 flex-wrap">
        {esAdminGeneral && (() => {
          const sin = products.filter((x) => x.activo !== false
            && ["precio_p1", "precio_p2", "precio_p3", "precio_p4"].some((k) => !(Number(x[k]) > 0))).length;
          return (
            <button data-testid="cargar-precios-btn" type="button" onClick={() => setPreciosOpen(true)}
              title="Descargar la plantilla con los productos sin precio, llenarla y subirla"
              className="border border-agro-yellow/40 hover:bg-agro-yellow/10 text-agro-yellow rounded-md px-3 py-2 text-sm flex items-center gap-2">
              Cargar precios desde Excel{sin > 0 && <span className="text-[11px] bg-agro-yellow/20 rounded px-1.5">{sin} sin precio</span>}
              {sinCosto > 0 && <span className="text-[11px] bg-red-500/20 text-red-300 rounded px-1.5" data-testid="sin-costo-count">{sinCosto} sin costo</span>}
            </button>
          );
        })()}
        <button
          data-testid="print-btn"
          onClick={doPrint}
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 active:scale-95"
        >
          <PrinterIcon size={16} weight="bold" /> Imprimir / PDF
        </button>
        </div>
      }
    >
      {movil && (
        <MovilFiltros busqueda={busqueda} categorias={categorias} marcas={marcas} filtroCat={filtroCat} setFiltroCat={setFiltroCat}
          filtroMarca={filtroMarca} setFiltroMarca={setFiltroMarca} niveles={niveles} toggleNivel={toggleNivel} canCosts={canCosts}
          showPC={showPC} setShowPC={setShowPC} showInactive={showInactive} onInactivos={(v) => { setShowInactive(v); load(v); }} />
      )}
      {movil && (
        <MovilLista loading={loading} items={filtered} niveles={niveles} showPC={pcVisible} onReactivate={reactivate} />
      )}
      {/* Toolbar — se oculta al imprimir */}
      {!movil && <div className="print:hidden bg-card border border-border/60 rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2 flex">
          <SearchBox busqueda={busqueda} placeholder="Buscar por código, nombre, SKU, código de barras…" testid="price-list-search" label="Buscar productos" className="min-w-0" />
        </div>
        <select value={filtroCat} onChange={(e) => setFiltroCat(e.target.value)}
          data-testid="filter-categoria"
          className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
          <option value="">Todas las categorías</option>
          {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filtroMarca} onChange={(e) => setFiltroMarca(e.target.value)}
          data-testid="filter-marca"
          className="bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
          <option value="">Todas las marcas</option>
          {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="flex items-center gap-2 justify-end">
          {canCosts && (
            <button
              type="button"
              data-testid="toggle-pc"
              onClick={() => setShowPC((v) => !v)}
              className={`px-3 py-2 text-xs rounded-md border flex items-center gap-1.5 transition-all ${
                showPC
                  ? "border-agro-yellow text-agro-yellow bg-agro-yellow/10"
                  : "border-border text-text-secondary hover:text-text-primary"
              }`}
              title="Mostrar / ocultar PC PEPS"
            >
              {showPC ? <EyeIcon size={14} /> : <EyeSlashIcon size={14} />} PC
            </button>
          )}
          <label className="flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
            <input type="checkbox" data-testid="toggle-inactive"
              checked={showInactive}
              onChange={(e) => { setShowInactive(e.target.checked); load(e.target.checked); }}
              className="accent-agro-green" />
            Inactivos
          </label>
        </div>
        <div className="md:col-span-5 flex flex-wrap items-center gap-2" data-testid="levels-select">
          <span className="text-[10px] uppercase tracking-wider text-text-muted">Niveles a imprimir:</span>
          {NIVELES.map((n) => (
            <label key={n.k} className={`px-2.5 py-1 text-xs rounded-md border cursor-pointer flex items-center gap-1.5 ${niveles.includes(n.k) ? "border-agro-green text-agro-green bg-agro-green/10" : "border-border text-text-secondary"}`}>
              <input type="checkbox" className="accent-agro-green" checked={niveles.includes(n.k)} onChange={() => toggleNivel(n.k)} data-testid={`level-${n.k}`} />
              {n.l}
            </label>
          ))}
        </div>
      </div>}

      {/* Documento imprimible (en el teléfono solo se usa al imprimir) — oscuro en pantalla, blanco al imprimir */}
      <div className={`${movil ? "hidden print:block " : ""}bg-card border border-border/60 rounded-lg overflow-hidden print:bg-white print:text-black print:rounded-none print:shadow-none print:border-0`} id="price-list-print">
        <div className="px-6 py-4 border-b border-border print:border-gray-300 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-agro-green flex items-center justify-center">
                <span className="text-black font-heading font-bold">A</span>
              </div>
              <div>
                <div className="font-heading font-bold text-lg text-text-primary print:text-black">{companyName}</div>
                <div className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-600">
                  Lista de precios oficial · {niveles.map((n) => n.toUpperCase()).join(" / ")}
                </div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-500">Emitida</div>
            <div className="text-sm font-medium text-text-primary print:text-black">{new Date().toLocaleDateString("es-HN", { year: "numeric", month: "long", day: "numeric" })}</div>
            <div className="text-[10px] text-text-muted print:text-gray-500">Precios en Lempiras (L.)</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="price-list-table">
            <thead className="bg-sidebar print:bg-gray-100 text-[10px] uppercase tracking-wider text-text-muted print:text-gray-700">
              <tr className="text-left">
                <th className="p-2 pl-5 w-24">Código</th>
                <th className="p-2">Producto</th>
                <th className="p-2 w-20">Unidad</th>
                <th className="p-2 w-16 text-center">ISV</th>
                {pcVisible && <th className="p-2 w-24 text-right bg-agro-yellow/10 print:bg-yellow-50 text-agro-yellow print:text-gray-700">PC PEPS</th>}
                {niveles.map((n) => <th key={n}className="p-2 w-24 text-right last:pr-5">{n.toUpperCase()}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={nCols} className="p-8 text-center text-text-muted print:text-gray-500">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={nCols} className="p-12 text-center text-text-muted print:text-gray-500">Sin productos para mostrar</td></tr>
              ) : groupByCategory(filtered).map((group) => (
                <GroupRows key={group.cat || "_"} group={group} showPC={pcVisible} niveles={niveles} nCols={nCols} onReactivate={reactivate} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-3 border-t border-border print:border-gray-300 flex items-center justify-between text-[10px] text-text-muted print:text-gray-500">
          <div>
            P0 = precio especial · P1–P4 = niveles de venta por tipo de cliente · Los precios pueden cambiar sin previo aviso.
          </div>
          <div>{filtered.length} ítems</div>
        </div>
      </div>

      {/* Estilos específicos de impresión */}
      <style>{`
        @media print {
          @page { size: A4; margin: 1.2cm; }
          html, body { background: white !important; color: black !important; }
          [data-testid="app-sidebar"], [data-testid="app-header"], .print\\:hidden { display: none !important; }
          [data-testid="page-main"] { padding: 0 !important; margin: 0 !important; }
          [data-testid="page-main"] > div { margin-left: 0 !important; }
          #price-list-print { background: white !important; color: black !important; border: 0 !important; box-shadow: none !important; }
          #price-list-print * { color: black !important; }
          #price-list-print .text-agro-green { color: #02BF44 !important; }
          #price-list-print .text-agro-yellow { color: black !important; }
          #price-list-print thead { background: #f3f4f6 !important; }
          tr { break-inside: avoid; }
          h2.cat-header { break-after: avoid; color: #02BF44 !important; }
        }
      `}</style>
      {preciosOpen && (
        <ExcelImportModal initialEntity="precios" initialFiltro={sinCosto > 0 ? "sin_costo" : "sin_precio"}
          onClose={() => { setPreciosOpen(false); load(); }}
          onDone={() => { setPreciosOpen(false); load(); }} />
      )}
    </Layout>
  );
}

/** Teléfono: búsqueda, categoría y marca, niveles de precio a mostrar (botones grandes) y opciones. */
function MovilFiltros({ busqueda, categorias, marcas, filtroCat, setFiltroCat, filtroMarca, setFiltroMarca, niveles, toggleNivel,
                        canCosts, showPC, setShowPC, showInactive, onInactivos }) {
  const sel = "w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm min-w-0";
  return (
    <div className="print:hidden bg-card border border-border/60 rounded-lg p-3 mb-3 space-y-3" data-testid="price-list-filtros-movil">
      <SearchBox busqueda={busqueda} placeholder="Código, nombre, SKU…" testid="price-list-search" label="Buscar productos" className="min-w-0 w-full" />
      <div className="grid grid-cols-2 gap-2">
        <select value={filtroCat} onChange={(e) => setFiltroCat(e.target.value)} data-testid="filter-categoria" aria-label="Categoría" className={sel}>
          <option value="">Categorías</option>
          {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filtroMarca} onChange={(e) => setFiltroMarca(e.target.value)} data-testid="filter-marca" aria-label="Marca" className={sel}>
          <option value="">Marcas</option>
          {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div data-testid="levels-select">
        <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Precios a mostrar</div>
        <div className="grid grid-cols-5 gap-1.5">
          {NIVELES.map((n) => {
            const on = niveles.includes(n.k);
            return (
              <button key={n.k} type="button" onClick={() => toggleNivel(n.k)} aria-pressed={on} data-testid={`level-${n.k}`}
                className={`min-h-[44px] rounded-md border text-sm font-semibold ${on ? "border-agro-green text-agro-green bg-agro-green/10" : "border-border text-text-muted"}`}>
                {n.l}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        {canCosts ? (
          <button type="button" data-testid="toggle-pc" onClick={() => setShowPC((v) => !v)} aria-pressed={showPC}
            className={`min-h-[40px] px-3 text-xs rounded-md border flex items-center gap-1.5 ${showPC ? "border-agro-yellow text-agro-yellow bg-agro-yellow/10" : "border-border text-text-secondary"}`}>
            {showPC ? <EyeIcon size={14} /> : <EyeSlashIcon size={14} />} Costo PC
          </button>
        ) : <span />}
        <label className="flex items-center gap-2 text-xs text-text-secondary min-h-[40px]">
          <input type="checkbox" data-testid="toggle-inactive" checked={showInactive} onChange={(e) => onInactivos(e.target.checked)} className="w-4 h-4 accent-agro-green" />
          Ver inactivos
        </label>
      </div>
    </div>
  );
}

/** Teléfono: un producto por tarjeta, agrupado por categoría, con los niveles elegidos (P1 resaltado). */
function MovilLista({ loading, items, niveles, showPC, onReactivate }) {
  if (loading) return <div className="print:hidden p-8 text-center text-sm text-text-muted">Cargando…</div>;
  if (!items.length) return <div className="print:hidden p-10 text-center text-sm text-text-muted">Sin productos para mostrar</div>;
  const cols = niveles.length + (showPC ? 1 : 0);
  return (
    <div className="print:hidden space-y-4" data-testid="price-list-movil">
      {groupByCategory(items).map((g) => (
        <section key={g.cat}>
          <h2 className="sticky top-16 z-10 bg-app/95 backdrop-blur py-1.5 text-xs font-heading font-semibold uppercase tracking-wider text-agro-green">
            {g.cat} <span className="text-text-muted font-normal normal-case">· {g.list.length}</span></h2>
          <ul className="space-y-2">
            {g.list.map((p) => (
              <li key={p.id} className={`bg-card border border-border/60 rounded-lg p-3 ${p.activo === false ? "opacity-70" : ""}`} data-testid={`price-card-${p.codigo}`}>
                <div className="text-sm font-medium text-text-primary leading-snug flex items-center gap-1.5">
                  {p.es_semilla && <FlowerIcon size={12} weight="fill" className="text-agro-yellow flex-shrink-0" />}{p.nombre || p.sku || "—"}</div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  <span className="font-mono">{p.codigo}</span>{p.marca ? ` · ${p.marca}` : ""} · {p.unidad_venta || p.unidad} · ISV {p.isv}%</div>
                <div className={`mt-2 grid gap-1.5 ${cols >= 3 ? "grid-cols-3" : cols === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {showPC && (
                    <div className="rounded-md bg-agro-yellow/10 px-2 py-1.5">
                      <div className="text-[10px] uppercase text-agro-yellow">Costo PC</div>
                      <div className="text-sm font-medium text-agro-yellow tabular-nums">{Number(p.costo_referencia) > 0 ? fmtL(p.costo_referencia) : "—"}</div>
                    </div>
                  )}
                  {niveles.map((k) => {
                    const v = p[`precio_${k}`];
                    return (
                      <div key={k} className={`rounded-md px-2 py-1.5 ${k === "p1" ? "bg-agro-green/10" : "bg-app"}`} data-testid={`price-card-${p.codigo}-${k}`}>
                        <div className={`text-[10px] uppercase ${k === "p1" ? "text-agro-green" : "text-text-muted"}`}>{k.toUpperCase()}</div>
                        <div className={`text-sm tabular-nums ${k === "p1" ? "font-semibold text-agro-green" : "text-text-primary"}`}>
                          {v ? fmtL(v) : k === "p0" ? <span className="text-[11px] text-text-muted">al vender</span> : "—"}</div>
                      </div>
                    );
                  })}
                </div>
                {p.activo === false && (
                  <button type="button" onClick={() => onReactivate(p)} data-testid={`price-reactivate-${p.codigo}`}
                    className="mt-2 min-h-[40px] w-full text-xs rounded-md border border-red-500/30 text-red-400 flex items-center justify-center gap-1.5">
                    <ArrowCounterClockwiseIcon size={12} /> Inactivo · reactivar</button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p className="text-[10px] text-text-muted text-center pb-2">P0 = precio especial · P1–P4 = niveles de venta por tipo de cliente · {items.length} productos</p>
    </div>
  );
}

function groupByCategory(items) {
  const m = new Map();
  items.forEach((p) => {
    const k = p.categoria || "Sin categoría";
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(p);
  });
  return Array.from(m.entries()).map(([cat, list]) => ({ cat, list }));
}

function GroupRows({ group, showPC, niveles, nCols, onReactivate }) {
  return (
    <>
      <tr className="bg-agro-green/10 print:bg-agro-green/5 border-t border-b border-border print:border-gray-200">
        <td colSpan={nCols} className="px-5 py-1.5">
          <h2 className="cat-header font-heading font-semibold text-xs uppercase tracking-wider text-agro-green">{group.cat}</h2>
        </td>
      </tr>
      {group.list.map((p) => (
        <tr key={p.id} className={`border-t border-border/40 print:border-gray-100 hover:bg-white/[0.02] ${p.activo === false ? "opacity-60" : ""}`} data-testid={`price-row-${p.codigo}`}>
          <td className="p-2 pl-5 font-mono text-xs text-text-primary print:text-black">
            <div className="flex items-center gap-1.5">
              {p.codigo}
              {p.es_semilla && <FlowerIcon size={10} weight="fill" className="text-agro-yellow print:text-yellow-600" />}
            </div>
          </td>
          <td className="p-2 text-text-primary print:text-black">
            <div className="text-sm flex items-center gap-2">
              {p.nombre || p.sku || "—"}
              {p.activo === false && (
                <button type="button" onClick={() => onReactivate(p)} data-testid={`price-reactivate-${p.codigo}`}
                  className="print:hidden text-[10px] px-1.5 py-0.5 rounded border border-red-500/30 text-red-400 hover:text-agro-green hover:border-agro-green/40 inline-flex items-center gap-1">
                  <ArrowCounterClockwiseIcon size={10} /> Inactivo · reactivar
                </button>
              )}
            </div>
            <div className="text-[10px] text-text-muted print:text-gray-500">{p.marca}{p.familia ? ` · ${p.familia}` : ""}</div>
          </td>
          <td className="p-2 text-xs text-text-secondary print:text-black">{p.unidad_venta || p.unidad}</td>
          <td className="p-2 text-center text-xs text-text-secondary print:text-black">{p.isv}%</td>
          {showPC && (
            <td className="p-2 text-right text-xs font-medium bg-agro-yellow/10 print:bg-yellow-50 text-agro-yellow print:text-black">
              {Number(p.costo_referencia) > 0
                ? <>{fmtL(p.costo_referencia)}{p.costo_origen === "catalogo" && <span className="block text-[10px] text-text-muted print:text-gray-500" title="El producto aún no tiene ingresos con costo">catálogo</span>}</>
                : "—"}
            </td>
          )}
          {niveles.map((k) => (
            <td key={k} className={`p-2 text-right text-sm last:pr-5 ${k === "p1" ? "font-semibold text-agro-green" : "text-text-primary"} print:text-black`}>
              {p[`precio_${k}`]
                ? fmtL(p[`precio_${k}`])
                : k === "p0" ? <span className="text-xs text-text-muted print:text-gray-400">se fija al vender</span> : "—"}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
